import { createContext, useContext, ReactNode, useMemo } from 'react';
import { Project } from '../types';

/**
 * Scope is the dashboard-wide answer to "which project am I looking at?"
 *
 * - `selectedProject` non-null → single-project view; pages filter by this.
 * - `selectedProject` null     → cross-project / "All projects" view; pages
 *                                that support it widen, pages that don't
 *                                show a friendly inline placeholder.
 *
 * URL-coupled pages (the Sessions list, the session detail) still derive
 * their own project from the URL; they sync the scope on mount so the
 * topbar chip stays consistent. Everything else just calls `useScope()`.
 */
export interface ScopeValue {
  selectedProject: Project | null;
  /** Every project this user can access. */
  accessibleProjects: Project[];
  /** Switch the active project. Pass null for the cross-project view. */
  setSelectedProject: (project: Project | null) => void;
  /** Convenience accessors. */
  projectId: string | null;
  isAllProjects: boolean;
  /** Look up a project by either its `_id` or its `projectId` slug. */
  resolveProject: (id: string | null | undefined) => Project | null;
}

const ScopeContext = createContext<ScopeValue | null>(null);

interface ScopeProviderProps {
  selectedProject: Project | null;
  accessibleProjects: Project[];
  setSelectedProject: (project: Project | null) => void;
  children: ReactNode;
}

export function ScopeProvider({
  selectedProject,
  accessibleProjects,
  setSelectedProject,
  children,
}: ScopeProviderProps) {
  const value = useMemo<ScopeValue>(() => {
    const resolveProject = (id: string | null | undefined): Project | null => {
      if (!id) return null;
      return (
        accessibleProjects.find((p) => p._id === id || p.projectId === id) || null
      );
    };
    return {
      selectedProject,
      accessibleProjects,
      setSelectedProject,
      projectId: selectedProject?._id ?? null,
      isAllProjects: !selectedProject,
      resolveProject,
    };
  }, [selectedProject, accessibleProjects, setSelectedProject]);

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

/**
 * Read the current dashboard scope. Throws if used outside the provider —
 * which only happens during boot before AppContent mounts.
 */
export function useScope(): ScopeValue {
  const ctx = useContext(ScopeContext);
  if (!ctx) {
    throw new Error('useScope must be used inside <ScopeProvider>');
  }
  return ctx;
}
